import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { ArrowLeft } from "lucide-react";
import { AUTHOR, AUTHOR_IMG, AFFILIATE_URL, AFF_DISCLOSURE } from "../lib/constants";
import { trackCta } from "../lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MdLink = ({ href, children }) => {
  const cleanHref = href ? href.replace(/%7B/gi, "{").replace(/%7D/gi, "}") : href;
  if (cleanHref && cleanHref.includes("umtugxsg.com")) {
    return (
      <span className="not-prose block my-6">
        <a
          href={cleanHref}
          target="_blank"
          rel="nofollow sponsored noopener"
          onClick={() => trackCta("blog-inline", cleanHref)}
          data-testid="cta-blog-affiliate"
          className="block w-fit bg-[#5A31F4] text-white hover:bg-[#4B29CD] rounded-md px-8 py-4 font-bold text-base no-underline transition-all duration-200 shadow-md hover:-translate-y-1"
        >
          {children}
        </a>
        <span className="block text-xs text-slate-500 mt-2" data-testid="blog-affiliate-disclosure">{AFF_DISCLOSURE}</span>
      </span>
    );
  }
  if (href && href.startsWith("/")) {
    return <Link to={href} className="text-[#1A56DB] font-semibold">{children}</Link>;
  }
  return <a href={href} target="_blank" rel="nofollow sponsored noopener" className="text-[#1A56DB] font-semibold">{children}</a>;
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPost(null);
    setError(false);
    window.scrollTo(0, 0);
    axios.get(`${API}/posts/${slug}`).then((res) => setPost(res.data)).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="blog-post-notfound">
        <h1 className="font-heading text-3xl font-bold text-[#0F172A]">Article not found</h1>
        <Link to="/blog" className="mt-4 inline-block text-[#1A56DB] font-semibold">← Back to blog</Link>
      </main>
    );
  }
  if (!post) {
    return <main className="max-w-3xl mx-auto px-4 py-24 text-slate-500" data-testid="blog-post-loading">Loading article…</main>;
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.meta_description,
    datePublished: post.published,
    author: { "@type": "Organization", name: "VoiceRanker.ai" },
  };

  return (
    <main className="bg-white" data-testid="blog-post-page">
      <Helmet>
        <title>{post.meta_title}</title>
        <meta name="description" content={post.meta_description} />
        <meta property="og:title" content={post.meta_title} />
        <meta property="og:description" content={post.meta_description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://voiceranker.ai/blog/${post.slug}`} />
        <link rel="canonical" href={`https://voiceranker.ai/blog/${post.slug}`} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to="/blog" data-testid="blog-post-back-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#1A56DB] transition-colors">
          <ArrowLeft className="w-4 h-4" /> All articles
        </Link>
        <div className="mt-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.15em]">
          <span className="text-[#5A31F4]">{post.category}</span>
          <span className="text-slate-300">•</span>
          <span className="text-slate-500">{post.read_time}</span>
        </div>
        <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-black tracking-tight leading-[1.1] text-[#0F172A]" data-testid="blog-post-title">
          {post.title}
        </h1>
        <div className="mt-6 flex items-center gap-3 pb-8 border-b border-slate-200">
          <img src={AUTHOR_IMG} alt={AUTHOR} className="w-10 h-10 rounded-full object-cover border border-slate-200" />
          <div className="text-sm">
            <p className="font-semibold text-[#0F172A]">{AUTHOR}</p>
            <p className="text-slate-500">Published {post.published} · Independent editorial review</p>
          </div>
        </div>
        {post.has_image && (
          <img src={`${API}/posts/${post.slug}/image`} alt={post.title} data-testid="blog-post-header-image" className="mt-8 w-full h-64 sm:h-80 object-cover rounded-lg border border-slate-200" />
        )}
        <div className="prose prose-slate prose-lg max-w-none mt-8 prose-headings:font-heading prose-headings:tracking-tight prose-headings:text-[#0F172A] prose-a:text-[#1A56DB] prose-strong:text-[#0F172A] prose-blockquote:border-[#5A31F4] prose-blockquote:bg-[#5A31F4]/5 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:not-italic prose-blockquote:rounded-r" data-testid="blog-post-body">
          <ReactMarkdown components={{ a: MdLink }}>{post.body}</ReactMarkdown>
        </div>
        <div className="mt-12 bg-[#0F172A] rounded-lg p-8 text-center" data-testid="blog-post-cta-box">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-white">Our Top Recommended AI Voice Platform for 2026</h2>
          <p className="mt-2 text-slate-300 text-sm">ElevenLabs — free to try, no credit card needed.</p>
          <a
            href={AFFILIATE_URL}
            target="_blank"
            rel="nofollow sponsored noopener"
            onClick={() => trackCta("blog-bottom", AFFILIATE_URL)}
            data-testid="cta-blog-bottom"
            className="mt-5 inline-block bg-[#5A31F4] text-white hover:bg-[#4B29CD] rounded-md px-8 py-3.5 font-bold transition-all duration-200 shadow-md hover:-translate-y-1"
          >
            Try ElevenLabs Free →
          </a>
          <p className="mt-3 text-xs text-slate-500 max-w-md mx-auto" data-testid="blog-bottom-affiliate-disclosure">{AFF_DISCLOSURE}</p>
        </div>
      </article>
    </main>
  );
}
